/** Official GSS admin boundaries — metadata-only contributions, no geometry UI. */

export const GSS_BOUNDARY_PLACE_CATEGORIES = ['country', 'region', 'district'] as const;

export type GssBoundaryPlaceCategory = (typeof GSS_BOUNDARY_PLACE_CATEGORIES)[number];

export type OfficialAreaLevel = GssBoundaryPlaceCategory;

export type MetadataUpdateIntent = 'description' | 'photos' | 'both';

/** Mirrors backend place_admin.PLACE_ADMIN_LEVEL. */
export const PLACE_ADMIN_LEVEL: Record<string, number> = {
  country: 0,
  region: 1,
  district: 2,
  city: 3,
  town: 4,
  village: 5,
  hamlet: 6,
  suburb: 7,
  neighbourhood: 8,
  neighborhood: 8,
  quarter: 9,
  locality: 8,
};

/** Direct map / contribution feature types without business hours or online presence. */
export const NON_BUSINESS_FEATURE_TYPES = new Set([
  'road',
  'place',
  'official_area',
  'transport_stop',
  'water',
  'waterway',
  'landcover',
  'landuse',
  'structure',
  'tree',
  'natural',
  'traffic_signal',
  'motorway_junction',
  'aeroway',
]);

/** Landmark registry groups that are geographic features, not POI businesses. */
export const NON_BUSINESS_LANDMARK_SOURCE_GROUPS = new Set([
  'water',
  'landcover',
  'landuse',
  'structure',
  'transport',
]);

const WATER_PLACE_CATEGORY_IDS = new Set([
  'lake', 'pond', 'river', 'stream', 'reservoir', 'lagoon', 'canal', 'dam', 'basin',
  'harbour', 'oxbow', 'lock', 'ditch', 'wastewater',
]);

const WATERWAY_PLACE_CATEGORY_IDS = new Set([
  'river', 'stream', 'canal', 'drain', 'ditch', 'dam', 'waterfall', 'dock', 'weir',
]);

const LANDCOVER_PLACE_CATEGORY_IDS = new Set([
  'wood', 'grassland', 'scrub', 'shrub', 'wetland', 'sand', 'mud', 'bare_rock', 'cliff',
  'ridge', 'peak', 'beach', 'shingle', 'heath', 'water', 'tree_row',
]);

const LANDUSE_PLACE_CATEGORY_IDS = new Set([
  'residential', 'commercial', 'retail', 'industrial', 'education', 'religious', 'cemetery',
  'government', 'public_office', 'military', 'farmland', 'farmyard', 'orchard', 'vineyard',
  'greenhouse_horticulture', 'meadow', 'grass', 'allotments', 'forest', 'recreation_ground',
  'park', 'playground', 'garden', 'village_green', 'pitch', 'stadium', 'golf_course',
  'brownfield', 'greenfield', 'construction', 'quarry', 'landfill', 'railway', 'harbour',
  'reservoir', 'basin',
]);

const STRUCTURE_PLACE_CATEGORY_IDS = new Set([
  'wall', 'fence', 'gate', 'lift_gate', 'hedge', 'retaining_wall', 'city_wall', 'bollard',
  'guard_rail', 'ditch', 'block', 'entrance', 'water_well',
]);

export function isGssBoundaryPlaceCategory(category: string | null | undefined): boolean {
  if (!category) return false;
  return (GSS_BOUNDARY_PLACE_CATEGORIES as readonly string[]).includes(category);
}

export function adminLevelToPlaceCategory(adminLevel: number): GssBoundaryPlaceCategory | null {
  if (adminLevel === 0) return 'country';
  if (adminLevel === 1) return 'region';
  if (adminLevel === 2) return 'district';
  return null;
}

export function resolveAdminLevel(
  category?: string | null,
  adminLevel?: number | null,
): number | null {
  if (adminLevel != null) return adminLevel;
  if (!category) return null;
  const level = PLACE_ADMIN_LEVEL[category.trim().toLowerCase()];
  return level ?? null;
}

export function isAdminPlaceType(placeType?: string | null): boolean {
  return placeType === 'place';
}

function normalizeId(value?: string | null): string {
  return (value ?? '').trim().toLowerCase();
}

function isNaturalAreaLandmarkCategory(category?: string | null): boolean {
  const cat = normalizeId(category);
  if (!cat) return false;
  return (
    WATER_PLACE_CATEGORY_IDS.has(cat)
    || WATERWAY_PLACE_CATEGORY_IDS.has(cat)
    || LANDCOVER_PLACE_CATEGORY_IDS.has(cat)
    || LANDUSE_PLACE_CATEGORY_IDS.has(cat)
    || STRUCTURE_PLACE_CATEGORY_IDS.has(cat)
  );
}

/** Whether a map place should show hours, contact, online presence, rating, and amenities. */
export function placeShowsBusinessDetails(options: {
  placeType?: string | null;
  category?: string | null;
}): boolean {
  const placeType = normalizeId(options.placeType);
  if (!placeType || placeType === 'street' || isAdminPlaceType(placeType)) return false;
  if (placeType === 'transit_stop') return false;
  if (NON_BUSINESS_FEATURE_TYPES.has(placeType)) return false;
  if (placeType === 'landmark' && isNaturalAreaLandmarkCategory(options.category)) return false;
  return true;
}

/** Whether contribution / preview flows should collect or show business contact fields. */
export function contributionFeatureShowsBusinessDetails(
  featureTypeId?: string | null,
  landmarkSourceGroup?: string | null,
): boolean {
  const ft = normalizeId(featureTypeId);
  if (!ft || NON_BUSINESS_FEATURE_TYPES.has(ft)) return false;
  if (ft === 'landmark') {
    const group = normalizeId(landmarkSourceGroup);
    if (NON_BUSINESS_LANDMARK_SOURCE_GROUPS.has(group)) return false;
    return true;
  }
  return true;
}

export function isGssOfficialAdminLevel(adminLevel?: number | null): boolean {
  return adminLevel != null && adminLevel <= 2;
}

export function metadataUpdateFields(intent: MetadataUpdateIntent): string[] {
  if (intent === 'description') return ['description'];
  if (intent === 'photos') return ['photos'];
  return ['description', 'photos'];
}
